import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Users, Target, Heart, Shield, 
  CheckCircle2, MapPin 
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We operate with complete transparency. No hidden fees, no surprises – just honest, fair offers.',
    },
    {
      icon: Heart,
      title: 'Compassion',
      description: 'We understand selling a home can be emotional. We treat every homeowner with respect and empathy.',
    },
    {
      icon: Target,
      title: 'Efficiency',
      description: 'Time matters. We streamline every step to give you the fastest, smoothest experience possible.',
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We work with you, not against you. Your needs and timeline always come first.',
    },
  ];

  const stats = [
    { number: '500+', label: 'Homes Purchased' },
    { number: '50', label: 'States Served' },
    { number: '24hrs', label: 'Avg. Offer Time' },
    { number: '98%', label: 'Client Satisfaction' },
  ];

  const coverage = [
    'California', 'Texas', 'Florida', 'New York', 'Arizona',
    'Nevada', 'Colorado', 'Georgia', 'North Carolina', 'Ohio',
    'Michigan', 'Pennsylvania', 'Illinois', 'Virginia', 'Washington',
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section with Gradient */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white via-emerald/5 to-white relative overflow-hidden">
        {/* Floating decorative elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div 
            className="absolute top-20 right-20 w-72 h-72 bg-emerald/10 rounded-full blur-3xl"
            animate={{ y: [0, -30, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 left-20 w-72 h-72 bg-gold/10 rounded-full blur-3xl"
            animate={{ y: [0, 30, 0], scale: [1, 0.9, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection direction="left">
              <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6">
                Our Story
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-darkgreen mb-6">
                About Green Palm Consulting
              </h1>
              <p className="font-subheading text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                Green Palm Consulting was founded with a simple mission: to make selling your 
                home as easy and stress-free as possible. We believe every homeowner deserves 
                a fair, transparent, and fast selling experience.
              </p>
              <p className="font-body text-gray-600 leading-relaxed">
                Since our founding, we've helped hundreds of homeowners across the United States 
                sell their properties quickly, without the hassle of traditional real estate. 
                No agents, no commissions, no repairs – just a fair cash offer and a closing 
                date that works for you.
              </p>
            </AnimatedSection>

            <AnimatedSection direction="right" delay={0.2}>
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
              >
                <motion.div 
                  className="absolute -inset-4 bg-gradient-to-br from-emerald/20 to-gold/20 rounded-3xl blur-xl opacity-50"
                  animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.7, 0.5] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Green Palm Consulting Team"
                  className="relative w-full h-auto rounded-2xl shadow-xl object-cover"
                />
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-darkgreen relative overflow-hidden">
        {/* Animated background */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-emerald/10 via-transparent to-gold/10"
          animate={{ opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div 
                  data-testid={`stat-item-${index}`} 
                  className="text-center"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="font-heading text-4xl md:text-5xl font-extrabold text-gold mb-2"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    {stat.number}
                  </motion.div>
                  <div className="font-subheading text-white/70 text-sm uppercase tracking-wider">
                    {stat.label}
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white via-gold/5 to-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div 
            className="absolute top-1/2 left-1/2 w-96 h-96 bg-emerald/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4 border border-gold/30">
              Our Mission
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-6">
              Making Home Selling Simple
            </h2>
            <p className="font-subheading text-base md:text-lg text-gray-600 leading-relaxed">
              We're committed to providing homeowners with a better alternative to traditional 
              real estate. Whether you're facing foreclosure, relocating, dealing with an 
              inherited property, or simply want to sell fast – we're here to help.
            </p>
          </AnimatedSection>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div 
                  data-testid={`value-item-${index}`}
                  className="bg-white rounded-2xl p-8 h-full border border-gray-100 card-premium"
                  whileHover={{ 
                    y: -10,
                    boxShadow: '0 25px 50px rgba(10, 61, 45, 0.12)',
                  }}
                >
                  <motion.div 
                    className="w-14 h-14 bg-emerald/10 rounded-xl flex items-center justify-center mb-6"
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(4, 99, 7, 0.2)' }}
                  >
                    <value.icon className="w-7 h-7 text-emerald" />
                  </motion.div>
                  <h3 className="font-heading text-xl font-bold text-darkgreen mb-3">
                    {value.title}
                  </h3>
                  <p className="font-body text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 via-emerald/5 to-gray-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection>
              <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6">
                Nationwide Service
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-darkgreen mb-6">
                We Buy Homes Across America
              </h2>
              <p className="font-body text-gray-600 mb-6 leading-relaxed">
                Green Palm Consulting serves homeowners in all 50 states. No matter where 
                your property is located, we can provide a fair cash offer and a smooth closing process.
              </p>
              
              {/* Headquarters Info */}
              <div className="bg-emerald/5 rounded-xl p-4 mb-8 border border-emerald/10">
                <p className="font-subheading font-semibold text-darkgreen text-sm mb-1">Headquarters</p>
                <p className="font-body text-gray-600 text-sm">
                  30 N Gould St Ste R, Sheridan, WY 82801<br />
                  <span className="text-emerald">+1 307-298-2879</span>
                </p>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {coverage.map((state, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.03, duration: 0.3 }}
                    whileHover={{ x: 5 }}
                  >
                    <MapPin className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span className="font-body text-sm text-gray-700">{state}</span>
                  </motion.div>
                ))}
                <motion.div 
                  className="flex items-center gap-2 col-span-2 sm:col-span-3 mt-2"
                  whileHover={{ x: 5 }}
                >
                  <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                  <span className="font-body text-sm text-gray-700 font-medium">
                    And all other states!
                  </span>
                </motion.div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <motion.div 
                className="relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100"
                whileHover={{ scale: 1.02 }}
              >
                {/* Stylized US Map Placeholder */}
                <div className="bg-gradient-to-br from-emerald/5 to-gold/5 rounded-2xl p-8 text-center">
                  <div className="text-6xl mb-4">🇺🇸</div>
                  <h3 className="font-heading text-2xl font-bold text-darkgreen mb-2">
                    50 States Covered
                  </h3>
                  <p className="font-body text-gray-600 text-sm">
                    From coast to coast, we're ready to buy your home
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <motion.div 
                    className="bg-emerald/10 rounded-xl p-4 text-center"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="font-heading text-2xl font-bold text-emerald">500+</div>
                    <div className="font-body text-xs text-gray-600">Homes Purchased</div>
                  </motion.div>
                  <motion.div 
                    className="bg-gold/10 rounded-xl p-4 text-center"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="font-heading text-2xl font-bold text-gold">$50M+</div>
                    <div className="font-body text-xs text-gray-600">Cash Paid to Sellers</div>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-darkgreen relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-0 left-0 w-96 h-96 bg-emerald/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-0 right-0 w-96 h-96 bg-gold/15 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"
            animate={{ scale: [1, 0.9, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6">
              Let's Work Together
            </h2>
            <p className="font-subheading text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Ready to sell your home the easy way? Get in touch with our team today 
              for a free, no-obligation cash offer.
            </p>
            <Link
              to="/get-offer"
              data-testid="about-cta-button"
              className="btn-premium inline-flex items-center justify-center bg-emerald hover:bg-emerald-dark text-white font-bold text-lg px-10 py-5 rounded-full group"
            >
              Get Your Free Offer
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
